import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from 'react-hot-toast'
import {
  UserIcon,
  BellIcon,
  ShieldCheckIcon,
  PaintBrushIcon,
  EyeIcon,
  SpeakerWaveIcon,
  KeyIcon,
  TrashIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import type { RootState } from '../store'
import { updateUser } from '../store/slices/authSlice'
import { useAuth } from '../hooks/useAuth'

interface SettingsSection {
  id: string
  title: string
  icon: React.ComponentType<any>
  description: string
}

const Settings: React.FC = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state: RootState) => state.auth)
  const { updateProfile, logout, changePassword } = useAuth()
  
  const [activeSection, setActiveSection] = useState('profile')
  const [isLoading, setIsLoading] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showPasswordChange, setShowPasswordChange] = useState(false)

  // Demo kullanıcı kontrolü
  const isDemoUser = user?.email === 'demo@lgs.com'

  // Form states
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    school: user?.school || '',
    grade: user?.grade || '',
    targetExam: (user as any)?.targetExam || '',
    studyGoal: (user as any)?.studyGoal || ''
  })

  const [preferences, setPreferences] = useState({
    theme: user?.preferences?.theme || 'system' as 'light' | 'dark' | 'system',
    language: (user?.preferences?.language || 'tr') as string,
    notifications: user?.preferences?.notifications || {
      email: true,
      push: true,
      sms: false
    },
    soundEffects: (user?.preferences as any)?.soundEffects || true,
    privacy: {
      profileVisible: user?.preferences?.privacy?.profileVisible || true,
      showProgress: user?.preferences?.privacy?.showProgress || true,
      showStats: user?.preferences?.privacy?.showStats || true
    }
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  const [notificationSettings, setNotificationSettings] = useState({
    studyReminders: true,
    examAlerts: true,
    achievementNotifications: true,
    socialUpdates: false,
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false
  })

  const settingsSections: SettingsSection[] = [
    {
      id: 'profile',
      title: 'Profil Bilgileri',
      icon: UserIcon,
      description: 'Kişisel bilgilerinizi düzenleyin'
    },
    {
      id: 'preferences',
      title: 'Tercihler',
      icon: PaintBrushIcon,
      description: 'Tema, dil ve görünüm ayarları'
    },
    {
      id: 'notifications',
      title: 'Bildirimler',
      icon: BellIcon,
      description: 'Bildirim tercihlerinizi yönetin'
    },
    {
      id: 'security',
      title: 'Güvenlik',
      icon: ShieldCheckIcon,
      description: 'Şifre ve güvenlik ayarları'
    },
    {
      id: 'privacy',
      title: 'Gizlilik',
      icon: EyeIcon,
      description: 'Gizlilik ve veri ayarları'
    },
    {
      id: 'account',
      title: 'Hesap',
      icon: TrashIcon,
      description: 'Hesap yönetimi ve silme'
    }
  ]

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        school: user.school || '',
        grade: user.grade || '',
        targetExam: (user as any)?.targetExam || '',
        studyGoal: (user as any)?.studyGoal || ''
      })
      setPreferences({
        theme: user.preferences?.theme || 'system',
        language: (user.preferences?.language as 'tr' | 'en') || 'tr',
        notifications: user.preferences?.notifications || {
          email: true,
          push: true,
          sms: false
        },
        soundEffects: (user.preferences as any)?.soundEffects || true,
        privacy: {
          profileVisible: user.preferences?.privacy?.profileVisible || true,
          showProgress: user.preferences?.privacy?.showProgress || true,
          showStats: user.preferences?.privacy?.showStats || true
        }
      })
    }
  }, [user])

  const handleProfileUpdate = async () => {
    if (isDemoUser) {
      toast.error('Demo hesabında profil düzenlenemez. Tam erişim için kayıt olun!')
      return
    }
    try {
      setIsLoading(true)
      await updateProfile(profileData)
      toast.success('Profil bilgileri güncellendi!')
    } catch (error: any) {
      toast.error(error.message || 'Profil güncellenirken bir hata oluştu')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePreferencesUpdate = async () => {
    if (isDemoUser) {
      toast.error('Demo hesabında tercihler değiştirilemez. Tam erişim için kayıt olun!')
      return
    }
    try {
      setIsLoading(true)
      await updateProfile({ preferences })
      dispatch(updateUser({ preferences }))
      toast.success('Tercihler güncellendi!')
    } catch (error: any) {
      toast.error(error.message || 'Tercihler güncellenirken bir hata oluştu')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasswordChange = async () => {
    if (isDemoUser) {
      toast.error('Demo hesabında şifre değiştirilemez. Tam erişim için kayıt olun!')
      return
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Yeni şifreler eşleşmiyor')
      return
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalıdır')
      return
    }

    try {
      setIsLoading(true)
      await changePassword(passwordData.currentPassword, passwordData.newPassword)
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      setShowPasswordChange(false)
      toast.success('Şifre başarıyla değiştirildi!')
    } catch (error: any) {
      toast.error(error.message || 'Şifre değiştirilirken bir hata oluştu')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      toast.success('Başarıyla çıkış yaptınız')
    } catch (error: any) {
      toast.error('Çıkış yapılırken bir hata oluştu')
    }
  }

  const handleDeleteAccount = async () => {
    // Bu fonksiyon gerçek uygulamada dikkatli bir şekilde implement edilmelidir
    toast.error('Hesap silme özelliği henüz aktif değil')
    setShowDeleteConfirm(false)
  }

  const renderProfileSection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4 sm:space-y-6"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
            Ad Soyad
          </label>
          <input
            type="text"
            value={profileData.name}
            onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
            disabled={isDemoUser}
            className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
              isDemoUser ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-600' : ''
            }`}
            placeholder="Adınızı ve soyadınızı girin"
          />
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
            E-posta
          </label>
          <input
            type="email"
            value={profileData.email}
            onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="E-posta adresiniz"
            disabled
          />
          <p className="text-xs text-gray-500 mt-1">E-posta adresi değiştirilemez</p>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
            Okul
          </label>
          <input
            type="text"
            value={profileData.school}
            onChange={(e) => setProfileData({ ...profileData, school: e.target.value })}
            disabled={isDemoUser}
            className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
              isDemoUser ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-600' : ''
            }`}
            placeholder="Okulunuzun adı"
          />
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
            Sınıf
          </label>
          <select
            value={profileData.grade}
            onChange={(e) => setProfileData({ ...profileData, grade: e.target.value })}
            disabled={isDemoUser}
            className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
              isDemoUser ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-600' : ''
            }`}
          >
            <option value="">Sınıf seçin</option>
            <option value="6">6. Sınıf</option>
            <option value="7">7. Sınıf</option>
            <option value="8">8. Sınıf</option>
          </select>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
            Hedef Sınav
          </label>
          <select
            value={profileData.targetExam}
            onChange={(e) => setProfileData({ ...profileData, targetExam: e.target.value })}
            disabled={isDemoUser}
            className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
              isDemoUser ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-600' : ''
            }`}
          >
            <option value="">Hedef sınav seçin</option>
            <option value="lgs">LGS</option>
            <option value="bursluluk">Bursluluk Sınavı</option>
            <option value="ozel-okul">Özel Okul Sınavı</option>
          </select>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
            Çalışma Hedefi
          </label>
          <input
            type="text"
            value={profileData.studyGoal}
            onChange={(e) => setProfileData({ ...profileData, studyGoal: e.target.value })}
            disabled={isDemoUser}
            className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
              isDemoUser ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-600' : ''
            }`}
            placeholder="Örn: Günde 2 saat çalışmak"
          />
        </div>
      </div>

      {isDemoUser && (
        <div className="flex items-center space-x-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 mb-4">
          <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
          <span className="text-xs text-amber-700 dark:text-amber-300">
            Demo hesabında profil düzenlenemez. Tam erişim için kayıt olun!
          </span>
        </div>
      )}
      
      <div className="flex justify-end">
        <button
          onClick={handleProfileUpdate}
          disabled={isLoading || isDemoUser}
          className={`w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base rounded-lg transition-colors ${
            isDemoUser 
              ? 'bg-gray-400 cursor-not-allowed text-white opacity-50' 
              : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
          }`}
        >
          {isLoading ? 'Güncelleniyor...' : 'Profili Güncelle'}
        </button>
      </div>
    </motion.div>
  )

  const renderPreferencesSection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4 sm:space-y-6"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
            Tema
          </label>
          <select
            value={preferences.theme}
            onChange={(e) => setPreferences({ ...preferences, theme: e.target.value as any })}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="light">Açık Tema</option>
            <option value="dark">Koyu Tema</option>
            <option value="system">Sistem Teması</option>
          </select>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
            Dil
          </label>
          <select
            value={preferences.language}
            onChange={(e) => setPreferences({ ...preferences, language: e.target.value as any })}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
            <BellIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <h4 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">Bildirimler</h4>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:block">Genel bildirim ayarları</p>
            </div>
          </div>
          <button
            onClick={() => setPreferences({ ...preferences, notifications: {
              email: !preferences.notifications.email,
              push: !preferences.notifications.push,
              sms: !preferences.notifications.sms
            } })}
            className={`relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors flex-shrink-0 ${
              (preferences.notifications.email || preferences.notifications.push || preferences.notifications.sms) ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform ${
                (preferences.notifications.email || preferences.notifications.push || preferences.notifications.sms) ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
            <SpeakerWaveIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <h4 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">Ses Efektleri</h4>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:block">Uygulama ses efektleri</p>
            </div>
          </div>
          <button
            onClick={() => setPreferences({ ...preferences, soundEffects: !preferences.soundEffects })}
            className={`relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors flex-shrink-0 ${
              preferences.soundEffects ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform ${
                preferences.soundEffects ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handlePreferencesUpdate}
          disabled={isLoading}
          className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? 'Güncelleniyor...' : 'Tercihleri Güncelle'}
        </button>
      </div>
    </motion.div>
  )

  const renderNotificationsSection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {Object.entries({
        studyReminders: 'Çalışma Hatırlatıcıları',
        examAlerts: 'Sınav Uyarıları',
        achievementNotifications: 'Başarım Bildirimleri',
        socialUpdates: 'Sosyal Güncellemeler',
        emailNotifications: 'E-posta Bildirimleri',
        pushNotifications: 'Push Bildirimleri',
        smsNotifications: 'SMS Bildirimleri'
      }).map(([key, label]) => (
        <div key={key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">{label}</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {key === 'studyReminders' && 'Çalışma zamanı geldiğinde bildirim al'}
              {key === 'examAlerts' && 'Yaklaşan sınavlar için uyarı al'}
              {key === 'achievementNotifications' && 'Yeni başarımlar için bildirim al'}
              {key === 'socialUpdates' && 'Arkadaş aktiviteleri için bildirim al'}
              {key === 'emailNotifications' && 'E-posta ile bildirim al'}
              {key === 'pushNotifications' && 'Tarayıcı bildirimleri al'}
              {key === 'smsNotifications' && 'SMS ile bildirim al'}
            </p>
          </div>
          <button
            onClick={() => setNotificationSettings({
              ...notificationSettings,
              [key]: !notificationSettings[key as keyof typeof notificationSettings]
            })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              notificationSettings[key as keyof typeof notificationSettings] ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                notificationSettings[key as keyof typeof notificationSettings] ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      ))}
    </motion.div>
  )

  const renderSecuritySection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4 sm:space-y-6"
    >
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 sm:p-4">
        <div className="flex items-start space-x-2 sm:space-x-3">
          <ExclamationTriangleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <h4 className="font-medium text-sm sm:text-base text-yellow-800 dark:text-yellow-200">Güvenlik Önemli</h4>
            <p className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              Hesabınızın güvenliği için güçlü bir şifre kullanın ve düzenli olarak değiştirin.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <button
          onClick={() => setShowPasswordChange(!showPasswordChange)}
          className="w-full flex items-center justify-between p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
            <KeyIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
            <div className="text-left min-w-0">
              <h4 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">Şifre Değiştir</h4>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:block">Hesap şifrenizi güncelleyin</p>
            </div>
          </div>
          <div className="text-gray-400 flex-shrink-0">
            {showPasswordChange ? <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" /> : <ArrowRightOnRectangleIcon className="h-4 w-4 sm:h-5 sm:w-5" />}
          </div>
        </button>

        <AnimatePresence>
          {showPasswordChange && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3 sm:space-y-4 p-3 sm:p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                  Mevcut Şifre
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Mevcut şifrenizi girin"
                  autoComplete="current-password"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                  Yeni Şifre
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Yeni şifrenizi girin"
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                  Yeni Şifre (Tekrar)
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  autoComplete="new-password"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Yeni şifrenizi tekrar girin"
                />
              </div>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={handlePasswordChange}
                  disabled={isLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                  className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
                </button>
                <button
                  onClick={() => {
                    setShowPasswordChange(false)
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
                  }}
                  className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                >
                  İptal
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )

  const renderPrivacySection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4 sm:space-y-6"
    >
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 sm:p-4">
        <div className="flex items-start space-x-2 sm:space-x-3">
          <InformationCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <h4 className="font-medium text-sm sm:text-base text-blue-800 dark:text-blue-200">Gizlilik Politikası</h4>
            <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-300 mt-1">
              Verilerinizin nasıl kullanıldığı hakkında daha fazla bilgi için gizlilik politikamızı inceleyin.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <div className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h4 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white mb-1 sm:mb-2">Veri Kullanımı</h4>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
            Çalışma verileriniz sadece performansınızı analiz etmek ve size daha iyi öneriler sunmak için kullanılır.
          </p>
          <ul className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li>• Çalışma süreleri ve performans verileri</li>
            <li>• Sınav sonuçları ve analiz verileri</li>
            <li>• Uygulama kullanım istatistikleri</li>
          </ul>
        </div>

        <div className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h4 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white mb-1 sm:mb-2">Veri Güvenliği</h4>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            Tüm verileriniz şifrelenerek güvenli sunucularda saklanır ve üçüncü taraflarla paylaşılmaz.
          </p>
        </div>
      </div>
    </motion.div>
  )

  const renderAccountSection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4 sm:space-y-6"
    >
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 sm:p-4">
        <div className="flex items-start space-x-2 sm:space-x-3">
          <ExclamationTriangleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <h4 className="font-medium text-sm sm:text-base text-red-800 dark:text-red-200">Dikkat</h4>
            <p className="text-xs sm:text-sm text-red-700 dark:text-red-300 mt-1">
              Hesabınızı sildiğinizde tüm verileriniz kalıcı olarak silinir ve geri alınamaz.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-between p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
            <ArrowRightOnRectangleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
            <div className="text-left min-w-0">
              <h4 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">Çıkış Yap</h4>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:block">Hesabınızdan güvenli çıkış yapın</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="w-full flex items-center justify-between p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
            <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <div className="text-left min-w-0">
              <h4 className="font-medium text-sm sm:text-base text-red-800 dark:text-red-200">Hesabı Sil</h4>
              <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 hidden sm:block">Hesabınızı kalıcı olarak silin</p>
            </div>
          </div>
        </button>
      </div>

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 max-w-md w-full mx-3 sm:mx-0"
            >
              <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 flex-shrink-0" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  Hesabı Sil
                </h3>
              </div>
              
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6">
                Bu işlem geri alınamaz. Hesabınız ve tüm verileriniz kalıcı olarak silinecek.
              </p>
              
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={handleDeleteAccount}
                  className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Evet, Sil
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                >
                  İptal
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return renderProfileSection()
      case 'preferences':
        return renderPreferencesSection()
      case 'notifications':
        return renderNotificationsSection()
      case 'security':
        return renderSecuritySection()
      case 'privacy':
        return renderPrivacySection()
      case 'account':
        return renderAccountSection()
      default:
        return renderProfileSection()
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Ayarlar</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1 sm:mt-2">
            Hesap bilgilerinizi ve tercihlerinizi yönetin
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-1 sm:space-y-2">
              {settingsSections.map((section) => {
                const Icon = section.icon
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-left transition-colors ${
                      activeSection === section.id
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-sm sm:text-base truncate">{section.title}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                        {section.description}
                      </div>
                    </div>
                  </button>
                )
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                  {settingsSections.find(s => s.id === activeSection)?.title}
                </h2>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
                  {settingsSections.find(s => s.id === activeSection)?.description}
                </p>
              </div>

              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Settings